import * as React from "react"
import { Head, Link, router } from "@inertiajs/react"
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  ArrowUpDown,
  Ban,
  CheckCircle2,
  Clock,
  Download,
  Loader2,
  MapPinCheck,
  RefreshCw,
  Search,
  XCircle,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import {
  DataCard,
  DataCardField,
  DataCardGrid,
  DataCardHeader,
  DataCardStatus,
  DataCardTitle,
} from "@/components/ui/data-card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { MobileFilterSheet } from "@/components/ui/mobile-filter-sheet"
import { MobileFilterSortBar } from "@/components/ui/mobile-filter-sort-bar"
import { MobileSortSheet, type SortOption } from "@/components/ui/mobile-sort-sheet"
import { useMobileFilterSort } from "@/hooks/use-mobile-filter-sort"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"

const SORT_OPTIONS: SortOption[] = [
  { sort: "layer0_flag_count", direction: "desc", label: "Flag terbanyak" },
  { sort: "layer0_flag_count", direction: "asc", label: "Flag paling sedikit" },
  { sort: "outlet_national_code", direction: "asc", label: "Kode A–Z" },
  { sort: "outlet_national_code", direction: "desc", label: "Kode Z–A" },
  { sort: "outlet_national_name", direction: "asc", label: "Nama A–Z" },
  { sort: "outlet_national_name", direction: "desc", label: "Nama Z–A" },
  { sort: "kabupaten", direction: "asc", label: "Kabupaten A–Z" },
  { sort: "layer1_verdict", direction: "asc", label: "Verdict A–Z" },
  { sort: "distance_m", direction: "asc", label: "Jarak terdekat" },
  { sort: "distance_m", direction: "desc", label: "Jarak terjauh" },
  { sort: "name_similarity", direction: "desc", label: "Similarity tertinggi" },
  { sort: "name_similarity", direction: "asc", label: "Similarity terendah" },
]

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type RunStatus = "pending" | "processing" | "completed" | "failed" | "cancelled"
type RunScope = "layer0" | "layer0_pilot"
type Verdict = "confirmed" | "likely" | "mismatch" | "not_found"

type Layer0Digest = {
  flag_counts?: Record<string, number>
  flagged_count?: number
  outlet_count?: number
  txn_window?: string
  checks_skipped?: string[]
  blank_code_rows?: number
  duplicate_code_rows?: number
}

type Layer1Digest = {
  pilot_total?: number
  checked_count?: number
  skipped_no_coords?: number
  hit_rate?: number
  verdict_counts?: Record<string, number>
  region_mismatch_counts?: Record<string, number>
  phone_promotions?: number
  closed_phantoms?: number
  cache_hits?: number
  errors?: number
  api_call_count?: number
  api_call_budget?: number
  note?: string
}

type RunDetail = {
  id: number
  period_label: string
  scope: RunScope
  status: RunStatus
  outlet_count: number
  processed_count: number
  layer0_flagged_count: number
  pilot_outlet_count: number
  api_call_count: number
  api_call_budget: number | null
  error_message: string | null
  layer1_note: string | null
  started_at: string | null
  finished_at: string | null
  created_at: string
  triggered_by: string | null
  summary_layer0: Layer0Digest | null
  summary_layer1: Layer1Digest | null
}

type ResultRow = {
  id: number
  outlet_national_code: string
  outlet_national_name: string | null
  outlet_national_status: string | null
  kabupaten: string | null
  top_paretto: string | null
  layer0_flags: string[]
  layer0_findings: Record<string, unknown>
  layer0_flag_count: number
  layer1_verdict: Verdict | null
  distance_m: number | null
  name_similarity: number | null
  matched_place_id: string | null
  matched_name: string | null
  google_business_status: string | null
  region_mismatch_level: string | null
  checked_at: string | null
  address: string | null
  kecamatan: string | null
  provinsi: string | null
  latitude: string | null
  longitude: string | null
}

type Filters = {
  flag: string | null
  verdict: string | null
  region: string | null
  kabupaten: string | null
  outlet_status: string | null
  q: string | null
  suspect: string | null
}

type BreakdownRow = {
  label: string
  checked: number
  verdicts: Record<string, number>
  hit_rate: number
}

const VERDICTS: Verdict[] = ["confirmed", "likely", "mismatch", "not_found"]

const VERDICT_LABELS: Record<string, string> = {
  confirmed: "Confirmed",
  likely: "Likely",
  mismatch: "Mismatch",
  not_found: "Not Found",
  unchecked: "Belum dicek",
}

const REGION_LABELS: Record<string, string> = {
  none: "Region sesuai",
  kecamatan: "Beda kecamatan",
  kabupaten: "Beda kabupaten",
  provinsi: "Beda provinsi",
  unresolved: "Tak terpetakan",
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function AdminOutletVerificationShow({
  run,
  results,
  results_total,
  page,
  per_page,
  sort,
  direction,
  filters,
  filter_options,
  suspect_count,
  has_pilot_results,
  breakdowns,
}: {
  run: RunDetail
  results: ResultRow[]
  results_total: number
  page: number
  per_page: number
  sort: string
  direction: "asc" | "desc"
  filters: Filters
  filter_options: {
    flags: { value: string; label: string }[]
    verdicts: string[]
    regions: string[]
    kabupatens: string[]
    statuses: string[]
  }
  suspect_count: number
  has_pilot_results: boolean
  breakdowns: { by_kabupaten: BreakdownRow[]; by_status: BreakdownRow[] } | null
}) {
  const [detail, setDetail] = React.useState<ResultRow | null>(null)
  const [qDraft, setQDraft] = React.useState(filters.q ?? "")
  React.useEffect(() => setQDraft(filters.q ?? ""), [filters.q])

  const flagLabels = React.useMemo(() => {
    const map: Record<string, string> = {}
    filter_options.flags.forEach((f) => (map[f.value] = f.label))
    return map
  }, [filter_options.flags])

  const isInFlight = run.status === "pending" || run.status === "processing"
  const layer0 = run.summary_layer0
  const layer1 = run.summary_layer1
  const pilotChecked = layer1?.checked_count ?? 0

  // -------------------------------------------------------------------------
  // Navigation (URL-driven filter/sort/page state)
  // -------------------------------------------------------------------------

  const FILTER_KEYS = ["flag", "verdict", "region", "kabupaten", "outlet_status", "q", "suspect"] as const

  function navigate(overrides: Record<string, string | number | null>) {
    const params: Record<string, string | number> = {}
    FILTER_KEYS.forEach((k) => {
      const v = filters[k]
      if (v) params[k] = v
    })
    if (sort !== "layer0_flag_count") params.sort = sort
    if (direction !== "desc") params.direction = direction
    if (page > 1) params.page = page
    Object.entries(overrides).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== "") params[k] = v as string | number
      else delete params[k]
    })
    router.get(`/admin/outlet-verification/${run.id}`, params as Record<string, string>, {
      preserveScroll: true,
    })
  }

  function handleSortColumn(col: string) {
    if (sort === col) {
      navigate({ direction: direction === "asc" ? "desc" : "asc", page: null })
    } else {
      navigate({ sort: col, direction: col === "layer0_flag_count" ? "desc" : "asc", page: null })
    }
  }

  // Raw download — deliberately outside the Inertia router. The active
  // dashboard filters ride along; the controller enforces suspect scope.
  function exportUrl(fmt: "csv" | "xlsx") {
    const params = new URLSearchParams()
    FILTER_KEYS.forEach((k) => {
      const v = filters[k]
      if (v) params.set(k, v)
    })
    params.set("format", fmt)
    return `/admin/outlet-verification/${run.id}/export?${params.toString()}`
  }

  function handleExport(fmt: "csv" | "xlsx") {
    window.location.href = exportUrl(fmt)
  }

  // -------------------------------------------------------------------------
  // Computed
  // -------------------------------------------------------------------------

  const hasActiveFilter = FILTER_KEYS.some((k) => !!filters[k])
  const { filterOpen, setFilterOpen, sortOpen, setSortOpen, activeFilterCount, applyFilters, resetFilters } =
    useMobileFilterSort(filters, navigate, FILTER_KEYS)
  const sortLabel =
    SORT_OPTIONS.find((o) => o.sort === sort && o.direction === direction)?.label ?? "Urutkan"
  const totalPages = Math.ceil(results_total / per_page)
  const pilotScoped = run.scope === "layer0_pilot"

  const pageTitle = `Run Verifikasi ${run.period_label}`
  const pageDescription = `Dashboard hasil verifikasi outlet periode ${run.period_label}: KPI Layer 0, verdict Google pilot, hit-rate, dan worklist export.`

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <>
      <Head title={pageTitle}>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
      </Head>
      <AppShell width="wide">
        {/* Header */}
        <div className="border-b border-hairline pb-6">
          <Link
            href="/admin/outlet-verification"
            className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink-body"
          >
            <ArrowLeft className="h-4 w-4" />
            Geo Verification
          </Link>
          <div className="mt-2 flex items-start gap-3">
            <MapPinCheck className="mt-1 h-6 w-6 shrink-0 text-ink-muted" />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-3">
                <h1>Run Verifikasi {run.period_label}</h1>
                <StatusBadge status={run.status} />
                <ScopeBadge scope={run.scope} />
              </div>
              <p className="mt-1 text-sm text-ink-muted">
                {run.triggered_by ? `Oleh ${run.triggered_by} · ` : ""}
                {formatDate(run.created_at)}
                {run.started_at && run.finished_at
                  ? ` · selesai dalam ${formatDuration(run.started_at, run.finished_at)}`
                  : ""}
              </p>
            </div>
          </div>
        </div>

        {/* Run-state banners */}
        {isInFlight && (
          <div className="mt-6 flex flex-wrap items-center gap-3 rounded-md border border-hairline bg-surface px-4 py-3">
            <Loader2 className="h-4 w-4 animate-spin text-ink-muted" />
            <p className="text-sm text-ink-body">
              Run masih berjalan ({formatCount(run.processed_count)} /{" "}
              {formatCount(run.outlet_count)} outlet) — angka di bawah belum final.
            </p>
            <Button
              variant="secondary"
              size="sm"
              className="ml-auto gap-1"
              onClick={() => router.reload()}
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Muat ulang
            </Button>
          </div>
        )}
        {run.status === "failed" && run.error_message && (
          <div className="mt-6 rounded-md border border-red-300 bg-surface px-4 py-3 dark:border-red-800">
            <p className="text-sm text-red-600 dark:text-red-400">{run.error_message}</p>
          </div>
        )}

        {/* KPI cards */}
        <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
          <KpiCard label="Outlet Dicek" value={formatCount(run.outlet_count)} />
          <KpiCard
            label="Bermasalah (Layer 0)"
            value={formatCount(run.layer0_flagged_count)}
            hint={
              run.outlet_count > 0
                ? `${formatPct(run.layer0_flagged_count / run.outlet_count)} dari total`
                : undefined
            }
          />
          <KpiCard
            label="Suspect (worklist)"
            value={formatCount(suspect_count)}
            hint="Flag Layer 0 atau Mismatch/Not Found"
          />
          {pilotScoped && (
            <KpiCard
              label="Pilot Diperiksa"
              value={formatCount(pilotChecked)}
              hint={
                layer1?.pilot_total != null
                  ? `dari ${formatCount(layer1.pilot_total)} outlet pareto`
                  : `dari ${formatCount(run.pilot_outlet_count)} outlet pareto`
              }
            />
          )}
          {pilotScoped && (
            <KpiCard
              label="Hit-Rate"
              value={layer1?.hit_rate != null ? formatPct(layer1.hit_rate) : "—"}
              hint="Confirmed + Likely dari yang diperiksa"
            />
          )}
          <KpiCard
            label="API Call"
            value={formatCount(run.api_call_count)}
            hint={run.api_call_budget != null ? `budget ${formatCount(run.api_call_budget)}` : undefined}
          />
        </div>

        {/* Layer-0 flag panel */}
        {layer0?.flag_counts && (
          <div className="mt-8">
            <h2>Temuan Layer 0</h2>
            <p className="mt-1 text-sm text-ink-muted">
              Klik sebuah flag untuk memfilter tabel hasil di bawah.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {Object.entries(layer0.flag_counts).map(([flag, count]) => {
                const active = filters.flag === flag
                return (
                  <button
                    key={flag}
                    type="button"
                    onClick={() => navigate({ flag: active ? null : flag, page: null })}
                    className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors ${
                      active
                        ? "border-accent bg-accent-faded text-ink-display"
                        : "border-hairline bg-surface text-ink-body hover:border-accent"
                    } ${count === 0 ? "opacity-50" : ""}`}
                  >
                    {flagLabels[flag] ?? flag}
                    <span className="tabular-nums font-medium">{formatCount(count)}</span>
                  </button>
                )
              })}
            </div>
            {(layer0.checks_skipped?.length ?? 0) > 0 && (
              <p className="mt-2 text-xs text-ink-muted">
                Pemeriksaan dilewati (sumber data kosong): {layer0.checks_skipped!.join(", ")}
              </p>
            )}
          </div>
        )}

        {/* Pilot hit-rate report */}
        {pilotScoped && (
          <div className="mt-8">
            <h2>Laporan Hit-Rate Pilot (Google Maps)</h2>
            {has_pilot_results && layer1 ? (
              <>
                {layer1.note && <p className="mt-1 text-sm text-ink-muted">{layer1.note}</p>}

                {/* Verdict distribution */}
                <div className="mt-4 grid gap-4 lg:grid-cols-2">
                  <div className="rounded-md border border-hairline bg-surface p-4">
                    <h3>Distribusi Verdict</h3>
                    <div className="mt-3 space-y-2">
                      {VERDICTS.map((v) => {
                        const count = layer1.verdict_counts?.[v] ?? 0
                        const share = pilotChecked > 0 ? count / pilotChecked : 0
                        const active = filters.verdict === v
                        return (
                          <button
                            key={v}
                            type="button"
                            onClick={() => navigate({ verdict: active ? null : v, page: null })}
                            className={`block w-full rounded-md border px-3 py-2 text-left transition-colors ${
                              active
                                ? "border-accent bg-accent-faded"
                                : "border-transparent hover:border-hairline"
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <VerdictBadge verdict={v} />
                              <span className="text-sm tabular-nums text-ink-body">
                                {formatCount(count)}{" "}
                                <span className="text-ink-muted">({formatPct(share)})</span>
                              </span>
                            </div>
                            <ShareBar value={share} tone={v} />
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  {/* Region mismatch + stats */}
                  <div className="rounded-md border border-hairline bg-surface p-4">
                    <h3>Region &amp; Statistik</h3>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {Object.entries(layer1.region_mismatch_counts ?? {}).map(([level, count]) => {
                        const active = filters.region === level
                        return (
                          <button
                            key={level}
                            type="button"
                            onClick={() => navigate({ region: active ? null : level, page: null })}
                            className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors ${
                              active
                                ? "border-accent bg-accent-faded text-ink-display"
                                : "border-hairline text-ink-body hover:border-accent"
                            } ${count === 0 ? "opacity-50" : ""}`}
                          >
                            {REGION_LABELS[level] ?? level}
                            <span className="tabular-nums font-medium">{formatCount(count)}</span>
                          </button>
                        )
                      })}
                    </div>
                    <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                      <StatRow label="Promosi via telepon" value={layer1.phone_promotions} />
                      <StatRow label="Phantom (tutup permanen)" value={layer1.closed_phantoms} />
                      <StatRow label="Cache place_id" value={layer1.cache_hits} />
                      <StatRow label="Error API" value={layer1.errors} />
                      <StatRow label="Tanpa koordinat valid" value={layer1.skipped_no_coords} />
                    </dl>
                  </div>
                </div>

                {/* Breakdown tables */}
                {breakdowns && (
                  <div className="mt-4 grid gap-4 lg:grid-cols-2">
                    <BreakdownTable title="Per Kabupaten" rows={breakdowns.by_kabupaten} />
                    <BreakdownTable title="Per Status Master" rows={breakdowns.by_status} />
                  </div>
                )}
              </>
            ) : (
              <div className="mt-3 rounded-md border border-hairline bg-surface px-4 py-3">
                <p className="text-sm text-ink-muted">
                  {run.layer1_note ??
                    "Belum ada hasil Layer 1 untuk run ini."}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Results */}
        <div className="mt-10">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <h2 className="mb-0">Hasil per Outlet</h2>
            <div className="ml-auto hidden gap-2 md:flex">
              <Button variant="secondary" size="sm" className="gap-1" onClick={() => handleExport("csv")}>
                <Download className="h-3.5 w-3.5" />
                Export CSV
              </Button>
              <Button variant="secondary" size="sm" className="gap-1" onClick={() => handleExport("xlsx")}>
                <Download className="h-3.5 w-3.5" />
                Export XLSX
              </Button>
            </div>
          </div>
          <p className="mb-4 text-sm text-ink-muted">
            Export berisi outlet suspect saja (flag Layer 0 atau verdict Mismatch/Not Found) dan
            mengikuti filter yang sedang aktif — {formatCount(suspect_count)} outlet suspect di run
            ini.
          </p>

          {/* Filter + Sort bar (mobile) */}
          <div className="mb-4 md:hidden">
            <MobileFilterSortBar
              filterCount={activeFilterCount}
              sortLabel={sortLabel}
              onFilterClick={() => setFilterOpen(true)}
              onSortClick={() => setSortOpen(true)}
            />
          </div>

          {/* Filter bar (desktop) */}
          <div className="mb-4 hidden flex-wrap items-center gap-3 md:flex">
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault()
                navigate({ q: qDraft || null, page: null })
              }}
            >
              <Input
                value={qDraft}
                onChange={(e) => setQDraft(e.target.value)}
                placeholder="Cari kode / nama outlet…"
                className="w-56"
                aria-label="Cari kode atau nama outlet"
              />
              <Button type="submit" variant="secondary" size="sm" className="gap-1">
                <Search className="h-3.5 w-3.5" />
                Cari
              </Button>
            </form>

            <Select
              value={filters.flag ?? ""}
              onChange={(e) => navigate({ flag: e.target.value || null, page: null })}
              className="w-52"
            >
              <option value="">Semua Flag</option>
              {filter_options.flags.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </Select>

            {(has_pilot_results || pilotScoped) && (
              <>
                <Select
                  value={filters.verdict ?? ""}
                  onChange={(e) => navigate({ verdict: e.target.value || null, page: null })}
                  className="w-40"
                >
                  <option value="">Semua Verdict</option>
                  {filter_options.verdicts.map((v) => (
                    <option key={v} value={v}>{VERDICT_LABELS[v] ?? v}</option>
                  ))}
                  <option value="unchecked">{VERDICT_LABELS.unchecked}</option>
                </Select>

                <Select
                  value={filters.region ?? ""}
                  onChange={(e) => navigate({ region: e.target.value || null, page: null })}
                  className="w-44"
                >
                  <option value="">Semua Region</option>
                  {filter_options.regions.map((r) => (
                    <option key={r} value={r}>{REGION_LABELS[r] ?? r}</option>
                  ))}
                </Select>
              </>
            )}

            <Select
              value={filters.kabupaten ?? ""}
              onChange={(e) => navigate({ kabupaten: e.target.value || null, page: null })}
              className="w-44"
            >
              <option value="">Semua Kabupaten</option>
              {filter_options.kabupatens.map((k) => (
                <option key={k} value={k}>{k}</option>
              ))}
            </Select>

            <Select
              value={filters.outlet_status ?? ""}
              onChange={(e) => navigate({ outlet_status: e.target.value || null, page: null })}
              className="w-36"
            >
              <option value="">Semua Status</option>
              {filter_options.statuses.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>

            <label className="flex items-center gap-2 font-normal text-ink-body">
              <Checkbox
                checked={filters.suspect === "1"}
                onChange={() =>
                  navigate({ suspect: filters.suspect === "1" ? null : "1", page: null })
                }
              />
              Hanya suspect
            </label>

            {hasActiveFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  navigate({
                    flag: null, verdict: null, region: null, kabupaten: null,
                    outlet_status: null, q: null, suspect: null, page: null,
                  })
                }
              >
                Reset filter
              </Button>
            )}
          </div>

          {results.length === 0 ? (
            <p className="text-sm text-ink-muted">
              {hasActiveFilter
                ? "Tidak ada hasil yang cocok dengan filter."
                : "Belum ada hasil untuk run ini."}
            </p>
          ) : (
            <>
              {/* Mobile card list */}
              <div className="space-y-3 md:hidden">
                {results.map((r) => (
                  <ResultCard key={r.id} result={r} flagLabels={flagLabels} onOpen={() => setDetail(r)} />
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden overflow-hidden rounded-md border border-hairline md:block">
                <table className="w-full text-sm">
                  <thead className="bg-surface">
                    <tr>
                      <SortableHeader
                        col="outlet_national_code" label="Kode"
                        sort={sort} direction={direction} onSort={handleSortColumn}
                      />
                      <SortableHeader
                        col="outlet_national_name" label="Nama"
                        sort={sort} direction={direction} onSort={handleSortColumn}
                      />
                      <SortableHeader
                        col="kabupaten" label="Kabupaten"
                        sort={sort} direction={direction} onSort={handleSortColumn}
                      />
                      <th className="px-4 py-2.5 text-left font-medium text-ink-muted">Status</th>
                      <SortableHeader
                        col="layer0_flag_count" label="Flag Layer 0"
                        sort={sort} direction={direction} onSort={handleSortColumn}
                      />
                      <SortableHeader
                        col="layer1_verdict" label="Verdict"
                        sort={sort} direction={direction} onSort={handleSortColumn}
                      />
                      <SortableHeader
                        col="distance_m" label="Jarak" align="right"
                        sort={sort} direction={direction} onSort={handleSortColumn}
                      />
                      <SortableHeader
                        col="name_similarity" label="Similarity" align="right"
                        sort={sort} direction={direction} onSort={handleSortColumn}
                      />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-hairline">
                    {results.map((r) => (
                      <tr
                        key={r.id}
                        className="cursor-pointer transition-colors hover:bg-surface"
                        onClick={() => setDetail(r)}
                      >
                        <td className="px-4 py-3 font-medium text-ink-display">
                          {r.outlet_national_code}
                        </td>
                        <td className="max-w-[220px] truncate px-4 py-3 text-ink-body" title={r.outlet_national_name ?? undefined}>
                          {r.outlet_national_name ?? "—"}
                        </td>
                        <td className="px-4 py-3 text-ink-body">{r.kabupaten ?? "—"}</td>
                        <td className="px-4 py-3 text-ink-body">{r.outlet_national_status ?? "—"}</td>
                        <td className="px-4 py-3">
                          <FlagBadges flags={r.layer0_flags} flagLabels={flagLabels} />
                        </td>
                        <td className="px-4 py-3">
                          {r.layer1_verdict ? <VerdictBadge verdict={r.layer1_verdict} /> : <span className="text-ink-muted">—</span>}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-ink-body">
                          {r.distance_m != null ? `${formatCount(r.distance_m)} m` : "—"}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-ink-body">
                          {r.name_similarity != null ? formatPct(r.name_similarity) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Summary + pagination */}
          {results_total > 0 && (
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-ink-muted">
                Menampilkan {(page - 1) * per_page + 1}–{Math.min(page * per_page, results_total)}{" "}
                dari {formatCount(results_total)} hasil
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => navigate({ page: page - 1 })}
                  >
                    Sebelumnya
                  </Button>
                  <span className="text-sm text-ink-muted">
                    Hal. {page} / {formatCount(totalPages)}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => navigate({ page: page + 1 })}
                  >
                    Berikutnya
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sticky export (mobile) */}
        <div className="fixed inset-x-4 bottom-4 z-30 flex gap-2 md:hidden">
          <Button variant="primary" className="flex-1 gap-1" onClick={() => handleExport("csv")}>
            <Download className="h-4 w-4" />
            CSV
          </Button>
          <Button variant="primary" className="flex-1 gap-1" onClick={() => handleExport("xlsx")}>
            <Download className="h-4 w-4" />
            XLSX
          </Button>
        </div>
        <div className="h-16 md:hidden" />

        {/* Mobile filter sheet */}
        <MobileFilterSheet
          open={filterOpen}
          onOpenChange={setFilterOpen}
          initial={{
            flag: filters.flag ?? "",
            verdict: filters.verdict ?? "",
            region: filters.region ?? "",
            kabupaten: filters.kabupaten ?? "",
            outlet_status: filters.outlet_status ?? "",
            q: filters.q ?? "",
            suspect: filters.suspect ?? "",
          }}
          onApply={applyFilters}
          onReset={resetFilters}
        >
          {(draft, setDraft) => (
            <>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-ink-display">Cari</span>
                <Input
                  value={draft.q}
                  onChange={(e) => setDraft({ ...draft, q: e.target.value })}
                  placeholder="Kode / nama outlet"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-ink-display">Flag Layer 0</span>
                <Select
                  value={draft.flag}
                  onChange={(e) => setDraft({ ...draft, flag: e.target.value })}
                >
                  <option value="">Semua Flag</option>
                  {filter_options.flags.map((f) => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </Select>
              </label>
              {(has_pilot_results || pilotScoped) && (
                <>
                  <label className="block">
                    <span className="mb-1 block text-sm font-medium text-ink-display">Verdict</span>
                    <Select
                      value={draft.verdict}
                      onChange={(e) => setDraft({ ...draft, verdict: e.target.value })}
                    >
                      <option value="">Semua Verdict</option>
                      {filter_options.verdicts.map((v) => (
                        <option key={v} value={v}>{VERDICT_LABELS[v] ?? v}</option>
                      ))}
                      <option value="unchecked">{VERDICT_LABELS.unchecked}</option>
                    </Select>
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-sm font-medium text-ink-display">Region</span>
                    <Select
                      value={draft.region}
                      onChange={(e) => setDraft({ ...draft, region: e.target.value })}
                    >
                      <option value="">Semua Region</option>
                      {filter_options.regions.map((r) => (
                        <option key={r} value={r}>{REGION_LABELS[r] ?? r}</option>
                      ))}
                    </Select>
                  </label>
                </>
              )}
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-ink-display">Kabupaten</span>
                <Select
                  value={draft.kabupaten}
                  onChange={(e) => setDraft({ ...draft, kabupaten: e.target.value })}
                >
                  <option value="">Semua Kabupaten</option>
                  {filter_options.kabupatens.map((k) => (
                    <option key={k} value={k}>{k}</option>
                  ))}
                </Select>
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-ink-display">Status Master</span>
                <Select
                  value={draft.outlet_status}
                  onChange={(e) => setDraft({ ...draft, outlet_status: e.target.value })}
                >
                  <option value="">Semua Status</option>
                  {filter_options.statuses.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </Select>
              </label>
              <label className="flex items-center gap-2 font-normal text-ink-body">
                <Checkbox
                  checked={draft.suspect === "1"}
                  onChange={() => setDraft({ ...draft, suspect: draft.suspect === "1" ? "" : "1" })}
                />
                Hanya suspect
              </label>
            </>
          )}
        </MobileFilterSheet>

        {/* Mobile sort sheet */}
        <MobileSortSheet
          open={sortOpen}
          onOpenChange={setSortOpen}
          current={{ sort, direction }}
          options={SORT_OPTIONS}
          onSelect={(opt) => {
            navigate({ sort: opt.sort, direction: opt.direction })
            setSortOpen(false)
          }}
        />

        {/* Drill-down dialog */}
        <ResultDetailDialog result={detail} flagLabels={flagLabels} onClose={() => setDetail(null)} />
      </AppShell>
    </>
  )
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function KpiCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-md border border-hairline bg-surface p-4">
      <div className="text-xs text-ink-muted">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-ink-display">{value}</div>
      {hint && <div className="mt-1 text-xs text-ink-muted">{hint}</div>}
    </div>
  )
}

function StatRow({ label, value }: { label: string; value?: number }) {
  return (
    <>
      <dt className="text-ink-muted">{label}</dt>
      <dd className="text-right tabular-nums text-ink-body">{formatCount(value ?? 0)}</dd>
    </>
  )
}

const VERDICT_BAR_CLASS: Record<Verdict, string> = {
  confirmed: "bg-green-500",
  likely: "bg-accent",
  mismatch: "bg-red-500",
  not_found: "bg-amber-500",
}

function ShareBar({ value, tone }: { value: number; tone: Verdict }) {
  return (
    <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-hairline">
      <div
        className={`h-full rounded-full ${VERDICT_BAR_CLASS[tone]}`}
        style={{ width: `${Math.min(value * 100, 100)}%` }}
      />
    </div>
  )
}

function BreakdownTable({ title, rows }: { title: string; rows: BreakdownRow[] }) {
  return (
    <div className="overflow-hidden rounded-md border border-hairline bg-surface">
      <div className="border-b border-hairline px-4 py-3">
        <h3>{title}</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-ink-muted">
              <th className="px-4 py-2 text-left font-medium">&nbsp;</th>
              <th className="px-2 py-2 text-right font-medium">Diperiksa</th>
              <th className="px-2 py-2 text-right font-medium">Conf.</th>
              <th className="px-2 py-2 text-right font-medium">Likely</th>
              <th className="px-2 py-2 text-right font-medium">Mism.</th>
              <th className="px-2 py-2 text-right font-medium">N.F.</th>
              <th className="px-4 py-2 text-right font-medium">Hit-Rate</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-hairline">
            {rows.map((row) => (
              <tr key={row.label}>
                <td className="max-w-[160px] truncate px-4 py-2 text-ink-body" title={row.label}>
                  {row.label}
                </td>
                <td className="px-2 py-2 text-right tabular-nums text-ink-body">
                  {formatCount(row.checked)}
                </td>
                {VERDICTS.map((v) => (
                  <td key={v} className="px-2 py-2 text-right tabular-nums text-ink-muted">
                    {formatCount(row.verdicts[v] ?? 0)}
                  </td>
                ))}
                <td className="px-4 py-2 text-right tabular-nums font-medium text-ink-display">
                  {formatPct(row.hit_rate)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function FlagBadges({
  flags,
  flagLabels,
  max = 2,
}: {
  flags: string[]
  flagLabels: Record<string, string>
  max?: number
}) {
  if (flags.length === 0) return <span className="text-ink-muted">—</span>
  const shown = flags.slice(0, max)
  const extra = flags.length - shown.length
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {shown.map((f) => (
        <Badge key={f} tone="signal">{flagLabels[f] ?? f}</Badge>
      ))}
      {extra > 0 && (
        <Badge tone="muted" title={flags.slice(max).map((f) => flagLabels[f] ?? f).join(", ")}>
          +{extra}
        </Badge>
      )}
    </span>
  )
}

function ResultCard({
  result,
  flagLabels,
  onOpen,
}: {
  result: ResultRow
  flagLabels: Record<string, string>
  onOpen: () => void
}) {
  return (
    <button type="button" className="block w-full text-left" onClick={onOpen}>
      <DataCard>
        <DataCardHeader>
          <DataCardTitle>{result.outlet_national_name ?? result.outlet_national_code}</DataCardTitle>
          <DataCardStatus>
            {result.layer1_verdict ? (
              <VerdictBadge verdict={result.layer1_verdict} />
            ) : (
              <Badge tone="muted">{formatCount(result.layer0_flag_count)} flag</Badge>
            )}
          </DataCardStatus>
        </DataCardHeader>
        <DataCardGrid>
          <DataCardField label="Kode" value={result.outlet_national_code} />
          <DataCardField label="Kabupaten" value={result.kabupaten ?? "—"} />
          <DataCardField label="Status" value={result.outlet_national_status ?? "—"} />
          <DataCardField
            label="Jarak"
            value={result.distance_m != null ? `${formatCount(result.distance_m)} m` : "—"}
          />
          <DataCardField
            wide
            label="Flag"
            value={
              result.layer0_flags.length > 0
                ? result.layer0_flags.map((f) => flagLabels[f] ?? f).join(", ")
                : "—"
            }
          />
        </DataCardGrid>
      </DataCard>
    </button>
  )
}

function ResultDetailDialog({
  result,
  flagLabels,
  onClose,
}: {
  result: ResultRow | null
  flagLabels: Record<string, string>
  onClose: () => void
}) {
  const findings = (result?.layer0_findings ?? {}) as Record<string, unknown>
  const txnSources = Array.isArray(findings.txn_sources) ? (findings.txn_sources as string[]) : null

  return (
    <Dialog open={result !== null} onOpenChange={(open) => { if (!open) onClose() }}>
      {result && (
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>{result.outlet_national_name ?? result.outlet_national_code}</DialogTitle>
            <DialogDescription>
              {result.outlet_national_code}
              {result.top_paretto?.trim().toUpperCase() === "YES" ? " · Pareto" : ""}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            {/* Identity */}
            <section>
              <h3>Data Master</h3>
              <dl className="mt-2 grid grid-cols-[150px_1fr] gap-x-3 gap-y-1.5 text-sm">
                <DetailRow label="Status" value={result.outlet_national_status} />
                <DetailRow label="Alamat" value={result.address} />
                <DetailRow label="Kecamatan" value={result.kecamatan} />
                <DetailRow label="Kabupaten" value={result.kabupaten} />
                <DetailRow label="Provinsi" value={result.provinsi} />
                <DetailRow
                  label="Koordinat"
                  value={
                    result.latitude || result.longitude
                      ? `${result.latitude ?? "?"}, ${result.longitude ?? "?"}`
                      : null
                  }
                />
              </dl>
            </section>

            {/* Layer 0 findings */}
            <section>
              <h3>Temuan Layer 0</h3>
              {result.layer0_flags.length === 0 ? (
                <p className="mt-2 text-sm text-ink-muted">Tidak ada flag — lolos semua pemeriksaan internal.</p>
              ) : (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {result.layer0_flags.map((f) => (
                    <Badge key={f} tone="signal">{flagLabels[f] ?? f}</Badge>
                  ))}
                </div>
              )}
              <dl className="mt-3 grid grid-cols-[150px_1fr] gap-x-3 gap-y-1.5 text-sm">
                {typeof findings.dup_exact_group_size === "number" && (
                  <DetailRow
                    label="Grup duplikat persis"
                    value={`${formatCount(findings.dup_exact_group_size)} outlet di koordinat sama`}
                  />
                )}
                {typeof findings.dup_rounded_group_size === "number" && (
                  <DetailRow
                    label="Grup duplikat ~11 m"
                    value={`${formatCount(findings.dup_rounded_group_size)} outlet dalam sel yang sama`}
                  />
                )}
                {typeof findings.dist_status_active === "boolean" && (
                  <DetailRow
                    label="Status di master dist"
                    value={findings.dist_status_active ? "Aktif" : "Nonaktif"}
                  />
                )}
                {txnSources && (
                  <DetailRow
                    label="Sumber transaksi"
                    value={txnSources.length > 0 ? txnSources.join(", ") : "tidak ada"}
                  />
                )}
              </dl>
            </section>

            {/* Layer 1 result */}
            <section>
              <h3>Hasil Google Maps (Layer 1)</h3>
              {result.layer1_verdict ? (
                <dl className="mt-2 grid grid-cols-[150px_1fr] gap-x-3 gap-y-1.5 text-sm">
                  <dt className="text-ink-muted">Verdict</dt>
                  <dd><VerdictBadge verdict={result.layer1_verdict} /></dd>
                  <DetailRow label="Tempat match" value={result.matched_name} />
                  <DetailRow label="Place ID" value={result.matched_place_id} mono />
                  <DetailRow
                    label="Jarak"
                    value={result.distance_m != null ? `${formatCount(result.distance_m)} m` : null}
                  />
                  <DetailRow
                    label="Similarity nama"
                    value={result.name_similarity != null ? formatPct(result.name_similarity) : null}
                  />
                  <DetailRow label="Status bisnis" value={result.google_business_status} />
                  <DetailRow
                    label="Region check"
                    value={
                      result.region_mismatch_level
                        ? REGION_LABELS[result.region_mismatch_level] ?? result.region_mismatch_level
                        : "Tak terpetakan"
                    }
                  />
                  <DetailRow
                    label="Diperiksa"
                    value={result.checked_at ? formatDate(result.checked_at) : null}
                  />
                </dl>
              ) : (
                <p className="mt-2 text-sm text-ink-muted">
                  Tidak diperiksa Google — di luar subset pilot, koordinat tidak valid, atau Layer 1
                  tidak berjalan.
                </p>
              )}
            </section>
          </div>
        </DialogContent>
      )}
    </Dialog>
  )
}

function DetailRow({ label, value, mono }: { label: string; value: string | null | undefined; mono?: boolean }) {
  return (
    <>
      <dt className="text-ink-muted">{label}</dt>
      <dd className={`text-ink-body ${mono ? "font-mono text-xs break-all" : ""}`}>
        {value?.toString().trim() ? value : "—"}
      </dd>
    </>
  )
}

function SortableHeader({
  col,
  label,
  sort,
  direction,
  onSort,
  align = "left",
}: {
  col: string
  label: string
  sort: string
  direction: "asc" | "desc"
  onSort: (col: string) => void
  align?: "left" | "right"
}) {
  const active = sort === col
  return (
    <th
      className={`cursor-pointer select-none px-4 py-2.5 font-medium text-ink-muted hover:text-ink-body text-${align}`}
      onClick={() => onSort(col)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active ? (
          direction === "asc" ? (
            <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowDown className="h-3 w-3" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-40" />
        )}
      </span>
    </th>
  )
}

function VerdictBadge({ verdict }: { verdict: Verdict }) {
  switch (verdict) {
    case "confirmed":
      return <Badge tone="success">Confirmed</Badge>
    case "likely":
      return <Badge tone="accent">Likely</Badge>
    case "mismatch":
      return <Badge tone="danger">Mismatch</Badge>
    case "not_found":
      return <Badge tone="signal">Not Found</Badge>
  }
}

function ScopeBadge({ scope }: { scope: RunScope }) {
  return scope === "layer0_pilot" ? (
    <Badge tone="accent">L0 + Pilot</Badge>
  ) : (
    <Badge tone="muted">Layer 0</Badge>
  )
}

function StatusBadge({ status }: { status: RunStatus }) {
  switch (status) {
    case "pending":
      return (
        <Badge tone="muted">
          <Clock className="mr-1 h-3 w-3" />
          Pending
        </Badge>
      )
    case "processing":
      return (
        <Badge tone="accent">
          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          Processing
        </Badge>
      )
    case "completed":
      return (
        <Badge tone="success">
          <CheckCircle2 className="mr-1 h-3 w-3" />
          Completed
        </Badge>
      )
    case "failed":
      return (
        <Badge tone="danger">
          <XCircle className="mr-1 h-3 w-3" />
          Failed
        </Badge>
      )
    case "cancelled":
      return (
        <Badge tone="muted">
          <Ban className="mr-1 h-3 w-3" />
          Dibatalkan
        </Badge>
      )
  }
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function formatCount(n: number): string {
  return n.toLocaleString("id-ID")
}

function formatPct(share: number): string {
  return `${(share * 100).toLocaleString("id-ID", { maximumFractionDigits: 1 })}%`
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("id-ID", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function formatDuration(startIso: string, endIso: string): string {
  const seconds = Math.max(0, (new Date(endIso).getTime() - new Date(startIso).getTime()) / 1000)
  if (seconds < 60) return `${Math.round(seconds)} dtk`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes} mnt ${Math.round(seconds % 60)} dtk`
  return `${Math.floor(minutes / 60)} jam ${minutes % 60} mnt`
}
